import React, { Component } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from './Button';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Error Boundary que captura erros do React e envia para o Sentry
 * Mantém o padrão visual do projeto usando os componentes existentes
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Atualiza o state para mostrar a UI de fallback
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Capturar erro no Sentry com contexto adicional
    Sentry.withScope(scope => {
      scope.setTag('errorBoundary', true);
      scope.setContext('errorInfo', {
        componentStack: errorInfo.componentStack,
        errorBoundary: this.constructor.name,
      });
      scope.setLevel('error');
      Sentry.captureException(error);
    });

    // eslint-disable-next-line no-console
    console.error('Error Boundary capturou um erro:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="mx-4 max-w-md rounded-xl border border-white/20 bg-white/70 p-8 shadow-md backdrop-blur-sm">
            <div className="text-center">
              {/* Ícone de erro */}
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>

              {/* Título */}
              <h1 className="mb-2 text-xl font-semibold text-gray-900">
                Oops! Algo deu errado
              </h1>

              {/* Descrição */}
              <p className="mb-6 text-gray-600">
                Ocorreu um erro inesperado. Nossa equipe foi notificada
                automaticamente e está trabalhando para resolver o problema.
              </p>

              {/* Botões de ação */}
              <div className="space-y-3">
                <Button onClick={this.handleRetry} className="w-full" size="lg">
                  Tentar Novamente
                </Button>

                <Button
                  onClick={this.handleReload}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  Recarregar Página
                </Button>
              </div>

              {/* Detalhes técnicos (apenas em desenvolvimento) */}
              {this.props.showDetails &&
                import.meta.env.VITE_NODE_ENV === 'development' &&
                this.state.error && (
                  <details className="mt-6 text-left">
                    <summary className="cursor-pointer text-sm text-gray-500">
                      Detalhes técnicos
                    </summary>
                    <div className="mt-2 rounded bg-gray-100 p-2 text-xs text-gray-700">
                      <div className="mb-2">
                        <strong>Erro:</strong> {this.state.error.toString()}
                      </div>
                      {this.state.errorInfo && (
                        <div>
                          <strong>Stack:</strong>
                          <pre className="mt-1 overflow-auto">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook para capturar erros em componentes funcionais
 */
export const useSentryErrorHandler = () => {
  return React.useCallback(
    (error: Error, errorInfo?: { [key: string]: unknown }) => {
      Sentry.withScope(scope => {
        if (errorInfo) {
          scope.setContext('errorInfo', errorInfo);
        }
        scope.setLevel('error');
        Sentry.captureException(error);
      });
    },
    []
  );
};

/**
 * Component wrapper que já vem com Error Boundary configurado
 */
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ReactNode
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};
